/**
 * App bar breadcrumbs for the Forge notebook page (`/notebooks/:notebookId`).
 */

import React, {type FC} from 'react';
import {useLocation, useRouteMatch} from 'react-router';

import {useMarimoNotebookRuntimePageQuery} from '../../../../generated/graphql';
import {
  FORGE_NOTEBOOK_PATH,
  NOTEBOOKS_HOME_PATHS,
  NOTEBOOKS_OVERVIEW_PATH,
} from '../../../../routes/notebooks/notebookPaths';
import {isMountedInShell} from '../../../../shellRemote';
import {decodeURIComponentSafe} from '../../../../util/urls';
import {removeNullUndefinedOrFalse} from '../../../../util/utility';
import Breadcrumbs, {type BreadcrumbPart} from '../../../Breadcrumbs';

/**
 * The notebook id from a Forge notebook URL, or undefined elsewhere. The route
 * pattern also matches the notebooks home pages (`/notebooks/overview`), which
 * the router resolves by order; this hook excludes them by path.
 */
export function useForgeNotebookId(): string | undefined {
  const {pathname} = useLocation();
  const match = useRouteMatch<{notebookId: string}>({
    exact: true,
    path: FORGE_NOTEBOOK_PATH,
  });
  const isHomePath = NOTEBOOKS_HOME_PATHS.includes(pathname.replace(/\/$/, ''));
  if (match == null || isHomePath) {
    return undefined;
  }
  return decodeURIComponentSafe(match.params.notebookId);
}

export type NavBreadcrumbsNotebooksProps = {
  notebookId: string | undefined;
};

export function useNavBreadcrumbsNotebooksParts({
  notebookId,
}: NavBreadcrumbsNotebooksProps): BreadcrumbPart[] {
  const {data} = useMarimoNotebookRuntimePageQuery({
    variables: {id: notebookId ?? ''},
    skip: notebookId == null,
  });
  const notebookName =
    notebookId != null && data?.notebook?.id === notebookId
      ? data.notebook.name
      : undefined;

  return removeNullUndefinedOrFalse([
    // The Forge shell header already shows the product as the first crumb.
    !isMountedInShell() && {
      label: 'Notebooks',
      to: NOTEBOOKS_OVERVIEW_PATH,
    },
    notebookName != null && {label: notebookName},
  ]);
}

const NavBreadcrumbsNotebooks: FC<NavBreadcrumbsNotebooksProps> = params => {
  const parts = useNavBreadcrumbsNotebooksParts(params);
  return <Breadcrumbs parts={parts} />;
};

export default NavBreadcrumbsNotebooks;
